import './style.scss';
import debounce from '@/tools/debounce';

/**
 * @typedef {Vue.VNode} LoaderVNode
 * @property {string} scrollID
 * @property {HTMLDivElement} wrapper
 * @property {HTMLDivElement} overlay
 * @property {HTMLDivElement} spinner
 */

let inited = false;
/** @var { {[string]: [HTMLElement, LoaderVNode] } } */
const vnodes = {};

const initScroll = () => {
    if (inited || !window || !window.addEventListener) {
        return;
    }

    window.addEventListener('scroll', () => scrollUpdate());
    window.addEventListener('resize', () => scrollUpdate());

    inited = true;
};

const scrollUpdate = debounce(
    /**
     * @param {HTMLElement} el
     * @param {LoaderVNode} vnode
     */
    (el, vnode) => {
        if (!window) {
            return;
        }

        initScroll();

        const currentVnodes = el && vnode
            ? { [vnode.scrollID]: [el, vnode] }
            : vnodes;

        const wh = window.innerHeight;
        const triggerMinHeight = window.innerHeight / 1.5;

        for (const id in currentVnodes) {
            if (!vnodes[id]) {
                delete vnodes[id];
                continue;
            }

            /** @var {HTMLElement} el */
            const el = vnodes[id][0];
            /** @var {LoaderVNode} vnode */
            const vnode = vnodes[id][1];
            const $spinner = vnode.spinner;

            const $target =  el.offsetHeight >= vnode.wrapper.offsetHeight ? el : vnode.wrapper;
            const h = $target.offsetHeight;


            if (h <= triggerMinHeight) {
                $spinner.style.top = null;
                continue;
            }

            const rect = $target.getBoundingClientRect();

            const visibleHeight = Math.max(0, rect.top > 0 ? Math.min(h, wh - rect.top) : Math.min(rect.bottom, wh));

            const scrollY = rect.top > 0 ? 0 : -rect.top;

            let elScrollY = scrollY + (visibleHeight / 2) - ($spinner.offsetHeight / 2);

            if (elScrollY < ($spinner.offsetHeight * 2)) {
                elScrollY = $spinner.offsetHeight * 2;
            } else if (elScrollY > $target.offsetHeight - ($spinner.offsetHeight * 2)) {
                elScrollY = $target.offsetHeight - ($spinner.offsetHeight * 2);
            }

            $spinner.style.top = `${elScrollY}px`;
        }
    },
    50,
    false,
    'vue-loader-scroll',
);

export default {
    bind(el, binding, vnode) {
        initScroll();

        vnode.scrollID = 'vue-scroll-' + Math.random().toString().replace(/0\./, '');

        // Create wrapper
        vnode.wrapper = document.createElement('div');
        vnode.wrapper.className = [
            'vue-loading',
            binding.modifiers.small ? 'vue-loading--small' : null,
            binding.modifiers.full ? 'vue-loading--full' : null,
            binding.modifiers.large ? 'vue-loading--large' : null,
        ].filter(v => v).join(' ');
        el.appendChild(vnode.wrapper);

        // Create overlay
        vnode.overlay = document.createElement('div');
        vnode.overlay.className = [
            'vue-loading-overlay',
            binding.modifiers.dark ? 'vue-loading-overlay--dark' : null,
            binding.modifiers['no-overlay'] ? 'transparent' : null,
        ].filter(v => v).join(' ');
        vnode.wrapper.appendChild(vnode.overlay);

        // Create spinner
        vnode.spinner = document.createElement('div');
        vnode.spinner.className = [
            'vue-loading-spinner',
            binding.modifiers.dark ? 'vue-loading-spinner--dark' : null,
        ].filter(v => v).join(' ');
        vnode.wrapper.appendChild(vnode.spinner);

        if (binding.value) {
            el.style.position = 'relative';
            el.style.cursor = 'wait';
            vnode.wrapper.classList.add('loader-show');
        }

        vnodes[vnode.scrollID] = [el, vnode];
        scrollUpdate();
    },
    inserted(el, binding, vnode) {
        scrollUpdate(el, vnode);
    },
    componentUpdated(el, binding, vnode) {
        scrollUpdate(el, vnode);
    },
    unbind(el, binding, vnode) {
        delete vnodes[vnode.scrollID];
        scrollUpdate(el, vnode);
    },
    update(el, binding, vnode, oldVnode) {
        vnode.scrollID = oldVnode.scrollID;
        vnode.wrapper = oldVnode.wrapper;
        vnode.overlay = oldVnode.overlay;
        vnode.spinner = oldVnode.spinner;

        if (binding.value) {
            el.style.position = 'relative';
            el.style.cursor = 'wait';
            vnode.wrapper.classList.add('loader-show');

            setTimeout(() => {
                vnode.spinner.classList.add('vue-loading-spinner--move-ready');
            }, 100);

            vnodes[vnode.scrollID] = [el, vnode];
        } else {
            vnode.spinner.classList.remove('vue-loading-spinner--move-ready');
            vnode.wrapper.classList.remove('loader-show');
            el.style.removeProperty('position');
            el.style.removeProperty('cursor');

            delete vnodes[vnode.scrollID];
        }

        scrollUpdate(el, vnode);
    },
};
